import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { SessionEndingModal } from '../../common/SessionEndingModal';
import { EMPLOYEE_LOGOUT_PARAM, SESSION_EXPIRED_PARAM, paths } from '../../common/constants/pathVariables.js';
import { useOnBoardingId } from '../../common/hooks/useOnBoardingId.js';
import { useSessionExpiredChecker } from '../../common/hooks/useSessionExpiredChecker';
import { loadingMsg, t } from '../../common/i18n/index.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { EoeLoginPath } from '../../public/site/path/Employee/EoeLoginPath.js';
import { resetErrors } from '../../selfservice/actions/index.js';
import { EoeHeader } from '../EoeHeader/EoeHeader.js';
import { Loading } from '../Loading/index.js';
import { NotificationContainer } from '../NotificationContainer/NotificationContainer.js';
import { SystemError, hasSystemError } from '../SystemError/SystemError.js';
import { EoeDialog } from './EoeDialog.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './EoeOutlet.scss';

export const EMPLOYEE_SUBSCRIPTION_PAIR_PATH = 'lisää';

export const isUserDetailsMissing = (user?: AuthenticatedUser | null): boolean =>
  !!user &&
  (user.mobile === undefined ||
    user.lastName === undefined ||
    user.email === undefined ||
    !user.firstName.length ||
    user.emailVerified === false);

const AuthorizationStatus = {
  AUTHORIZED: 'AUTHORIZED',
  UNAUTHORIZED: 'UNAUTHORIZED',
};

const EoeView = ({ employeeAuthenticationStatus }: { employeeAuthenticationStatus: string | undefined }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { anonymousUser, authenticatedUser } = useAuth();
  const authenticatedErrors = authenticatedUser?.errors;
  const anonymousErrors = anonymousUser?.errors;

  useEffect(() => {
    const decodedPathname = decodeURI(pathname);
    if (
      anonymousUser?.ssoSessionValid &&
      decodedPathname !== paths.EMPLOYEE_OWN_INFO &&
      !decodedPathname.startsWith(paths.EMPLOYEE_EMAIL_VERIFICATION) &&
      isUserDetailsMissing(authenticatedUser)
    ) {
      navigate(paths.EMPLOYEE_OWN_INFO, { replace: true });
    }
  }, [anonymousUser, authenticatedUser, navigate, pathname]);

  if (!employeeAuthenticationStatus) {
    return (
      <div id="elisaid-login-form-loading">
        <div className="ea-fgrid ea-fgrid--center">
          <div className="ea-fgrid__item ea-fgrid__item--center ea-fgrid__item--block ea-fgrid__item--column">
            <span className="ea-loading" aria-label={t.KW12(loadingMsg)} />
          </div>
        </div>
      </div>
    );
  } else if (employeeAuthenticationStatus === AuthorizationStatus.AUTHORIZED && hasSystemError(authenticatedErrors)) {
    return (
      <SystemError
        errors={authenticatedErrors}
        onButtonClick={() => {
          navigate(paths.EMPLOYEE_HOME);
          dispatch(resetErrors());
        }}
      />
    );
  } else if (employeeAuthenticationStatus === AuthorizationStatus.UNAUTHORIZED && hasSystemError(anonymousErrors)) {
    return (
      <SystemError
        errors={anonymousErrors}
        onButtonClick={() => {
          dispatch(resetErrors());
          if (pathname !== paths.EMPLOYEE_HOME) {
            navigate(paths.EMPLOYEE_HOME);
          }
        }}
      />
    );
  } else if (employeeAuthenticationStatus === AuthorizationStatus.AUTHORIZED) {
    return (
      <>
        <SessionEndingModal isEmployee={true} />
        <Outlet />
      </>
    );
  } else {
    return <EoeLoginPath />;
  }
};

export const EoeOutlet = () => {
  useOnBoardingId();
  useSessionExpiredChecker();
  const { ssoSessionValid, elisaIdV2LoginProcessing, preElisaIdV2AuthLoading } = useAuth();
  const { search } = useLocation();

  const employeeAuthenticationStatus = () => {
    if (search === EMPLOYEE_LOGOUT_PARAM || search === SESSION_EXPIRED_PARAM) {
      return AuthorizationStatus.UNAUTHORIZED;
    }
    if (elisaIdV2LoginProcessing) {
      return undefined;
    }
    return ssoSessionValid ? AuthorizationStatus.AUTHORIZED : AuthorizationStatus.UNAUTHORIZED;
  };

  if (preElisaIdV2AuthLoading && search !== EMPLOYEE_LOGOUT_PARAM && search !== SESSION_EXPIRED_PARAM) {
    return <Loading big={true} topPadding={4} />;
  }
  return (
    <div id="employee" className="of-site--employee">
      <EoeHeader />
      <div className="of-employee">
        <NotificationContainer />
        <EoeDialog />
        <EoeView employeeAuthenticationStatus={employeeAuthenticationStatus()} />
      </div>
    </div>
  );
};
